import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gift, Copy, Check, Sparkles, MessageCircle, Mail, Smartphone, Trophy, Users } from "lucide-react";

export default function PatientReferrals() {
  const { slug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [unread, setUnread] = useState(0);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [t, r, n] = await Promise.all([
      api.get(`/tenants/by-slug/${slug}`),
      api.get("/patient/referrals"),
      api.get("/patient/notifications"),
    ]);
    setTenant(t.data);
    setData(r.data);
    setUnread(n.data.filter((x) => !x.read).length);
  };
  useEffect(() => { load().catch((e) => toast.error(apiErrorMessage(e, "Could not load referrals"))); /* eslint-disable-next-line */ }, [slug]);

  const copyLink = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  const links = [
    { to: `/c/${slug}/dashboard`, label: "Dashboard" },
    { to: `/c/${slug}/rewards`, label: "Rewards" },
    { to: `/c/${slug}/family`, label: "Family" },
    { to: `/c/${slug}/refer`, label: "Refer" },
    { to: `/c/${slug}/profile`, label: "Profile" },
  ];

  if (!data) {
    return (
      <div className="min-h-screen bg-mr-bg">
        <TopBar tenant={tenant} links={links} unread={unread} onUnreadChange={setUnread} />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center text-mr-secondary">Loading referrals…</div>
      </div>
    );
  }

  const clinicName = tenant?.name || "MedRewards";
  const msg = `Join me at ${clinicName} on MedRewards and we both earn ${data.bonus_per_referral} loyalty points. Sign up here: ${data.link || ""}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const smsUrl = `sms:?&body=${encodeURIComponent(msg)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(`Join me at ${clinicName} on MedRewards`)}&body=${encodeURIComponent(msg)}`;

  return (
    <div className="min-h-screen bg-mr-bg">
      <TopBar tenant={tenant} links={links} unread={unread} onUnreadChange={setUnread} />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <span className="label-eyebrow text-mr-accent">Invite friends, earn points</span>
          <h1 className="font-display text-4xl font-bold text-mr-primary mt-2">Refer a friend</h1>
          <p className="text-mr-secondary mt-2 max-w-xl">Share your code. Every friend who joins {clinicName} earns you <b className="text-mr-primary">{data.bonus_per_referral} points</b> — auto-credited to your account.</p>
        </div>

        {/* Hero share card */}
        <div className="rounded-3xl p-8 sm:p-10 text-white shadow-[0_30px_80px_-40px_rgba(15,137,100,0.6)] relative overflow-hidden mb-10" style={{ background: `linear-gradient(135deg, ${tenant?.brand_color || "#0F8964"} 0%, ${tenant?.accent_color || "#0284C7"} 100%)` }}>
          <Sparkles className="absolute -top-10 -right-10 h-44 w-44 text-white/10" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.22em] opacity-80">Your referral code</p>
            <p data-testid="referral-code" className="font-display text-5xl font-bold mt-2 tracking-wide">{data.code || "—"}</p>

            <p className="text-xs uppercase tracking-[0.22em] opacity-80 mt-7">Share this link</p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <div className="flex-1 bg-white/15 backdrop-blur rounded-xl px-4 py-3 text-sm break-all" data-testid="referral-link">{data.link || "Link unavailable"}</div>
              <Button data-testid="referral-copy-btn" onClick={copyLink} disabled={!data.link} className="rounded-xl bg-white text-mr-primary hover:bg-white/90 h-12 px-5">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <a data-testid="referral-share-whatsapp" href={whatsappUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors rounded-full px-4 py-2 text-sm">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </a>
              <a data-testid="referral-share-sms" href={smsUrl} className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors rounded-full px-4 py-2 text-sm">
                <Smartphone className="h-3.5 w-3.5" /> SMS
              </a>
              <a data-testid="referral-share-email" href={mailUrl} className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors rounded-full px-4 py-2 text-sm">
                <Mail className="h-3.5 w-3.5" /> Email
              </a>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-mr p-5">
            <div className="flex items-center gap-2 text-mr-secondary text-xs uppercase tracking-wider">
              <Users className="h-3.5 w-3.5" /> Friends joined
            </div>
            <p className="font-display text-3xl font-bold text-mr-primary mt-2" data-testid="ref-stat-count">{data.total_referrals}</p>
          </div>
          <div className="bg-white rounded-2xl border border-mr p-5">
            <div className="flex items-center gap-2 text-mr-secondary text-xs uppercase tracking-wider">
              <Trophy className="h-3.5 w-3.5" /> Total points earned
            </div>
            <p className="font-display text-3xl font-bold text-mr-primary mt-2" data-testid="ref-stat-earned">{data.total_earned.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl border border-mr p-5">
            <div className="flex items-center gap-2 text-mr-secondary text-xs uppercase tracking-wider">
              <Gift className="h-3.5 w-3.5" /> Per referral
            </div>
            <p className="font-display text-3xl font-bold text-mr-primary mt-2">+{data.bonus_per_referral}</p>
            <p className="text-xs text-mr-secondary mt-1">points credited automatically</p>
          </div>
        </div>

        {/* Referral history */}
        <h2 className="font-display text-xl font-semibold text-mr-primary mb-3">Your referrals</h2>
        {data.referrals.length === 0 ? (
          <div className="bg-white border border-mr rounded-2xl p-12 text-center" data-testid="ref-empty">
            <div className="h-14 w-14 rounded-full bg-mr-primary/10 flex items-center justify-center mx-auto mb-4">
              <Gift className="h-6 w-6 text-mr-primary" />
            </div>
            <p className="font-display text-lg font-semibold text-mr-primary">No referrals yet</p>
            <p className="text-mr-secondary text-sm mt-1">Share your code above. We'll list your friends here as they sign up.</p>
          </div>
        ) : (
          <div className="bg-white border border-mr rounded-2xl divide-y divide-mr-border" data-testid="ref-list">
            {data.referrals.map((r) => (
              <div key={r.id} data-testid={`ref-item-${r.id}`} className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-mr-primary/10 text-mr-primary font-display font-semibold flex items-center justify-center flex-shrink-0">
                    {(r.name || "F")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-mr-primary truncate">{r.name}</p>
                    <p className="text-xs text-mr-secondary">Joined {new Date(r.joined_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className="text-mr-success font-display font-semibold whitespace-nowrap">+{r.points_credited} pts</span>
              </div>
            ))}
          </div>
        )}

        {/* Hidden field for clipboard fallback */}
        <Input className="sr-only" tabIndex={-1} aria-hidden value={data.link || ""} readOnly />
      </div>
    </div>
  );
}
